import React from "react";
import styled from "styled-components";
import { Title, Body } from "@vds/typography";
import { Icon } from "@vds/icons";
import { IMAGE_GUIDELINES_TEXT } from "../../../constants/enrolledAppearanceConstants";

const Container = styled.div`
  padding: 16px 0;
  width: 100%;
  max-width: 461px;
  min-height: 821px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e6e6e6;
  margin: 12px 0 16px 0;
`;

const GuidelinesImage = styled.img`
  width: 100%;
  max-width: 640px;
  border-radius: 8px;
  display: block;
  margin: 8px 0 16px 0;
`;

// Inline styles converted to styled components
const IntroContainer = styled.div`
  margin-bottom: 12px;
`;

const SectionDividerGreen = styled.div`
  border-bottom: 3px solid #008331;
  height: 3px;
  width: 100%;
`;

const SectionDividerOrange = styled.div`
  border-bottom: 3px solid #FF8027;
  height: 3px;
  margin-top: 20px;
  width: 100%;
`;

const PlaceholderBox = styled.div`
  width: 100%;
  height: 100%;
`;

// Do's layout
const DoSection = styled.div`
  margin-top: 16px;
`;

const DoColumns = styled.div``;

const DoColumnsInner = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const DoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DoImageWrapper = styled.div`
  width: 221px;
  height: 166px;
  border-radius: 0;
  overflow: hidden;
  background: #f2f2f2;
  border: 1px solid #e6e6e6;
`;

const DoImage = styled.img`
  width: 221px;
  height: 166px;
  object-fit: cover;
  display: block;
`;

const DoCaptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconCircle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff; /* white background */
  border: 1px solid #1bb55c; /* green border */
  display: flex;
  align-items: center;
  justify-content: center;
`;
const IconCircleDont = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff; /* white background */
  border: 1px solid #FF8027; /* orange border */
  display: flex;
  align-items: center;
  justify-content: center;
`;
const BottomDivider = styled.div`
  border-bottom: 1px solid #D8DADA;
  height: 1px;
  margin: 20px 0;
  width: 100%;
`;
/**
 * ImageGuidelines
 * Displays recommended guidelines for uploading appearance images.
 * Props:
 * - maxSize: number (MB) for max file size
 * - supportedTypes: array of strings representing allowed file types
 */
const ImageGuidelines = ({ maxSize = 10, supportedTypes = ["JPG", "JPEG", "PNG"], guidelinesImageSrc, doImages=[], doCaptions = [], dontImages = [], dontCaptions = [] }) => {
  return (
    <Container>
      <Header>
        <Title size="large" bold>{IMAGE_GUIDELINES_TEXT.TITLE}</Title>
      </Header>
      <Divider />
      {guidelinesImageSrc ? (
        <GuidelinesImage src={guidelinesImageSrc} alt={IMAGE_GUIDELINES_TEXT.ALT_BANNER} />
      ) : null}
      <IntroContainer>
        <Body size="large">
          {IMAGE_GUIDELINES_TEXT.INTRO}
        </Body>
      </IntroContainer>
      <SectionDividerGreen />
       <Body size="large" bold color="#008331">
        {IMAGE_GUIDELINES_TEXT.DO_TITLE}
      </Body>
      {/* Do's content: two columns with 8px gap; each column has image (221x166) and icon+text */}
      <DoSection>
        <DoColumns>
          <DoColumnsInner>
            {Array.from({ length: Math.max(doImages.length, doCaptions.length, 2)}).map((_, i) => (
              <DoColumn key={i}>
                <DoImageWrapper>
                  {doImages[i] ? (
                    <DoImage src={doImages[i]} alt={`${IMAGE_GUIDELINES_TEXT.ALT_DO_PREFIX} ${i + 1}`} />
                  ) : (
                    <PlaceholderBox />
                  )}
                </DoImageWrapper>
                <DoCaptionRow>
                  <IconCircle>
                    {/* Inline SVG checkmark to ensure visibility inside 20x20 circle */}
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8.5L6.5 12L13 4" stroke="#1bb55c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </IconCircle>
                  <Body size="small">{doCaptions[i]}</Body>
                </DoCaptionRow>
              </DoColumn>
            ))}
          </DoColumnsInner>
        </DoColumns>
      </DoSection>
  <SectionDividerOrange />
       <Body size="large" bold color="#FF8027">
        {IMAGE_GUIDELINES_TEXT.DONT_TITLE}
      </Body>
      <DoSection>
        <DoColumns>
          <DoColumnsInner>
            {Array.from({ length: Math.max(dontImages.length, dontCaptions.length, 2)}).map((_, i) => (
              <DoColumn key={i}>
                <DoImageWrapper>
                  {dontImages[i] ? (
                    <DoImage src={dontImages[i]} alt={`${IMAGE_GUIDELINES_TEXT.ALT_DONT_PREFIX} ${i + 1}`} />
                  ) : (
                    <PlaceholderBox />
                  )}
                </DoImageWrapper>
                <DoCaptionRow>
                  <IconCircleDont>
                    {/* Inline slash sized to touch the circle border */}
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 17 L18 2" stroke="#FF8027" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </IconCircleDont>
                  <Body size="small">{dontCaptions[i]}</Body>
                </DoCaptionRow>
              </DoColumn>
            ))}
          </DoColumnsInner>
        </DoColumns>
      </DoSection>
     <BottomDivider />
    </Container>
  );
};

export default ImageGuidelines;
